import { Button, Col, Form, FormLabel, Row } from "react-bootstrap";
import DateSelector from "../../components/common/DateSelector";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faFileExport, faSearch } from "@fortawesome/free-solid-svg-icons";
import { SubmittedDataService } from "../../../services/SubmittedDataService";
import { ISubmittedData } from "../../../models/Interfaces";
import { CellProps, Column, Row as TableRow } from "react-table";
import FilteredTable from "../../components/common/FilteredTable";
import moment from "moment";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./submittedData.module.scss";

class DateSearch {
  public dateFrom: Date | null = null;
  public dateTo: Date | null = null;
}

const SubmittedData = () => {
  const [dateSearch, setDateSearch] = useState<DateSearch | null>(null);
  const [submittedData, setSubmittedData] = useState<Array<ISubmittedData>>([]);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const sdsRef = useRef<SubmittedDataService | null>(null);

  const getSubmittedPreview = (row: ISubmittedData): string => {
    const entries = Object.entries(row.FormData ?? {});

    if (entries.length === 0) {
      return "No submitted values";
    }

    const preview = entries
      .slice(0, 2)
      .map(([key, value]) => {
        const joinedValue = Array.isArray(value) ? value.join(", ") : String(value ?? "");
        return `${key}: ${joinedValue}`;
      })
      .join(" | ");

    return entries.length > 2 ? `${preview} ...` : preview;
  };

  const formatDate = (value: string | null | undefined): string => {
    return value ? moment(value).format("DD-MM-YYYY") : "";
  };

  const columns: Column<ISubmittedData>[] = useMemo(() => [
    {
      Header: "",
      id: "expanddata",
      Cell: ({ row }: CellProps<ISubmittedData>) => (
        <button
          type="button"
          className={styles.expandButton}
          onClick={() => setExpandedRow(expandedRow === row.index ? null : row.index)}
          title={expandedRow === row.index ? "Collapse submission details" : "Expand submission details"}
        >
          <FontAwesomeIcon icon={expandedRow === row.index ? faChevronUp : faChevronDown} />
        </button>
      )
    },
    {
      Header: "Form Name",
      accessor: "FormName"
    },
    {
      Header: "Submitted Data",
      id: "submittedpreview",
      Cell: ({ row }: CellProps<ISubmittedData>) => (
        <span className={styles.previewValue}>{getSubmittedPreview(row.original)}</span>
      )
    },
    {
      Header: "Submitted",
      id: "submitteddate",
      Cell: ({ row }: CellProps<ISubmittedData>) => (
        <span>{formatDate(row.original.DateAdded)}</span>
      )
    },
    {
      Header: "Status",
      accessor: "StatusDescription"
    },
    {
      Header: "Notification Sent",
      id: "notificationsent",
      Cell: ({ row }: CellProps<ISubmittedData>) => (
        <span>{formatDate(row.original.DateSent)}</span>
      )
    },
    {
      Header: "Sent To",
      id: "senttolist",
      Cell: ({ row }: CellProps<ISubmittedData>) => (
        <span className={styles.previewValue}>{row.original.SentToList?.join(", ") || ""}</span>
      )
    }
  ], [expandedRow]);

  const handleDateSelection = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (dateSearch) {
      const { name, value } = event.target;
      if (name) {
        setDateSearch(prev => ({
          ...prev as DateSearch,
          [name]: value ? new Date(value) : null
        }));
      }
    }
  };

  const searchData = async (): Promise<void> => {
    if (sdsRef.current && dateSearch) {
      const res = await sdsRef.current.getSubmittedData(dateSearch.dateFrom, dateSearch.dateTo);
      setSubmittedData(res);
      setExpandedRow(null);
      setHasSearched(true);
    }
  };

  const expandedRowContent = (row: TableRow<ISubmittedData>): ReactNode => {
    const subdata = row.original.FormData;
    const entries = Object.entries(subdata ?? {});

    return entries.length > 0 ? (
      <div className={styles.expandedCard}>
        {entries.map(([key, value]) => (
          <Row key={`exprow-${row.original.Id}-${key}`} className={styles.expandedRow}>
            <Col md={12} lg={3}>
              <span className={styles.expandedLabel}>{key}</span>
            </Col>
            <Col md={12} lg={9}>
              <span className={styles.expandedValue}>{Array.isArray(value) ? value.join(", ") : String(value ?? "")}</span>
            </Col>
          </Row>
        ))}
      </div>
    ) : (
      <div className={styles.expandedEmpty}>
        <AppEmptyState
          compact
          title="No form data found"
          message="This submission does not contain any stored field values."
        />
      </div>
    );
  };

  const exportData = (): void => {
    const escapeCSVValue = (value: any): string => {
      if (value === null || value === undefined) return '""';
      if (typeof value === "object") {
        return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
      }
      return `"${String(value).replace(/"/g, '""')}"`;
    };

    if (submittedData.length > 0) {
      const headers: string[] = Object.keys(submittedData[0]);
      const csvRows = [
        headers.join(","),
        ...submittedData.map((row: any) =>
          headers.map((fieldName) => escapeCSVValue(row[fieldName])).join(",")
        ),
      ];

      const data = csvRows.join("\n");
      downloadCSV("form-submissions", data);
    }
  };

  const downloadCSV = (filename: string, csvdata: string): void => {
    const blob = new Blob([csvdata], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (dateSearch === null) {
      setDateSearch(new DateSearch());
    }

    if (!sdsRef.current) {
      sdsRef.current = new SubmittedDataService();
    }
  }, []);

  return (
    <div className={styles.page} id="submitted-form-data">
      <section className={styles.searchSection}>
        <div className={styles.searchFields}>
          <div>
            <FormLabel>Date from</FormLabel>
            <DateSelector
              onDateSelected={handleDateSelection}
              placeHolder="select date from..."
              id="dateFrom"
              name="dateFrom"
              dateFormat="dd-MM-yyyy"
              value={dateSearch?.dateFrom ?? null}
              showTimeInput={false}
              showTimeSelect={false}
            />
          </div>
          <div>
            <FormLabel>Date to</FormLabel>
            <DateSelector
              onDateSelected={handleDateSelection}
              placeHolder="select date to..."
              id="dateTo"
              name="dateTo"
              dateFormat="dd-MM-yyyy"
              value={dateSearch?.dateTo ?? null}
              showTimeInput={false}
              showTimeSelect={false}
              startDate={dateSearch?.dateFrom ?? undefined}
            />
          </div>
          <div className={styles.searchActions}>
            <Button variant="primary" title="search submitted data" onClick={searchData}>
              <FontAwesomeIcon icon={faSearch} />
              Search
            </Button>
          </div>
        </div>
      </section>

      <section className="app-list-section">
        <div className="app-list-section__header">
          <div>
            <h2 className="app-list-section__title">Submission Results</h2>
            <p className="app-list-section__summary">
              Review form submissions, expand individual entries to inspect their field values, and export the current result set when needed.
            </p>
          </div>
        </div>
        <div className="app-list-section__body">
          {submittedData.length > 0 ? (
            <FilteredTable<ISubmittedData>
              columns={columns}
              data={submittedData}
              noDataMessage="No submitted data found"
              showAddButton={true}
              addButtonText="Export"
              addButtonIcon={faFileExport}
              addButtonHandler={exportData}
              detailRow={expandedRow}
              detailRowRender={expandedRowContent}
              cellClass="text-start"
              pageSize={50}
              showPagerTop={true}
              showPagerBottom={true}
              filterPlaceHolder="filter submissions..."
            />
          ) : (
            <div className={styles.resultsEmpty}>
              <AppEmptyState
                title={hasSearched ? "No submissions found" : "Search submitted data"}
                message={hasSearched
                  ? "No form submissions matched the selected date range."
                  : "Choose a date range and run a search to review submitted form data for this site."}
                compact
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SubmittedData;
